import { DeepMap } from 'react-hook-form';
import { SelectOptionElement } from '@odin-labs/components';
import {
  CCureEntityType,
  JobsiteUpdateInput,
  WorkerCardType,
} from 'apollo/generated/client-operations';
import { ensureNonUndefinedFields } from 'utils';
import { FormInputTypes, TypedFormInputs } from 'components/form';
import { getCCureEntityMappingsOptions } from 'containers/jobsiteConfiguration/helpers/utils';
import {
  CCureEntityMapping,
  CCureEntitySelectOptionElement,
  EditJobsiteConfigurationFormData,
} from 'containers/jobsiteConfiguration/types';
import {
  getCCureEnvironmentsOptions,
  getWorkerCardFormatOptions,
  WorkerCardFormatOption,
} from 'containers/worker/helpers/utils';
import { Jobsite, UserData } from './types';
import {
  getCCureSectionInputsHook,
  getCCureSectionDefaultValues,
  getCCureSectionUpdateInput,
} from './cCureSection';

export type EditJobsiteConfigTabFormData = Pick<
  EditJobsiteConfigurationFormData,
  'cCure'
>;

export type JobsiteSelectOptions = {
  workerCardFormatsOptions: WorkerCardFormatOption[];
  cCureEnvironmentsOptions: SelectOptionElement[];
  cCureClearancesOptions: CCureEntitySelectOptionElement[];
  cCureControllersOptions: CCureEntitySelectOptionElement[];
  cCureDoorsOptions: CCureEntitySelectOptionElement[];
  cCureInboundAreasOptions: CCureEntitySelectOptionElement[];
  cCureOutboundAreasOptions: CCureEntitySelectOptionElement[];
};

export const getDropdownsOptions = (args: {
  userData: UserData;
  jobsite: Jobsite;
}): JobsiteSelectOptions => {
  const { userData } = args;
  const workerCardFormats = userData?.workerCardFormats.edges
    .map(({ node }) => node)
    .filter((wcf) => wcf.cardType !== WorkerCardType.Bluetooth);
  const cCureEnvironments = userData?.cCureEnvironments.edges.map(
    ({ node }) => node,
  );
  const entityMappingsByType: Partial<
    Record<CCureEntityType, CCureEntityMapping[]>
  > =
    userData?.cCureEntityMappingOptions.edges.reduce(
      (
        groups: Record<CCureEntityType, CCureEntityMapping[]>,
        { node: mapping },
      ) => {
        const { entityType } = mapping;
        groups[entityType] = groups[entityType] ?? []; // eslint-disable-line no-param-reassign
        groups[entityType].push(mapping);
        return groups;
      },
      {} as Record<CCureEntityType, CCureEntityMapping[]>,
    ) ?? {};

  return {
    workerCardFormatsOptions: getWorkerCardFormatOptions(workerCardFormats),
    cCureEnvironmentsOptions: getCCureEnvironmentsOptions(cCureEnvironments),
    cCureClearancesOptions: getCCureEntityMappingsOptions(
      entityMappingsByType[CCureEntityType.Clearance],
      false,
    ),
    cCureControllersOptions: getCCureEntityMappingsOptions(
      entityMappingsByType[CCureEntityType.Controller],
    ),
    cCureDoorsOptions: getCCureEntityMappingsOptions(
      entityMappingsByType[CCureEntityType.Door],
    ),
    cCureInboundAreasOptions: getCCureEntityMappingsOptions(
      entityMappingsByType[CCureEntityType.InArea],
    ),
    cCureOutboundAreasOptions: getCCureEntityMappingsOptions(
      entityMappingsByType[CCureEntityType.OutArea],
    ),
  };
};

type GetFormInputsArgs = JobsiteSelectOptions & {
  jobsite: Jobsite;
};

export const getFormInputs = (
  args: GetFormInputsArgs,
): TypedFormInputs<EditJobsiteConfigTabFormData> => {
  const {
    workerCardFormatsOptions,
    cCureEnvironmentsOptions,
    cCureClearancesOptions: clearanceOptions,
    cCureControllersOptions: controllerOptions,
    cCureDoorsOptions: doorOptions,
    cCureInboundAreasOptions: inboundAreaOptions,
    cCureOutboundAreasOptions: outboundAreaOptions,
  } = args;

  const cCureSectionInputs = getCCureSectionInputsHook({
    workerCardFormatsOptions,
    clearanceOptions,
    cCureEnvironmentsOptions,
    controllerOptions,
    doorOptions,
    inboundAreaOptions,
    outboundAreaOptions,
  });

  return {
    cCure: {
      element: FormInputTypes.Section,
      layout: '',
      elementProps: {
        title: 'C-CURE',
      },
      children: cCureSectionInputs,
    },
  };
};

type GetDefaultValuesArgs = {
  jobsite: Jobsite;
} & JobsiteSelectOptions;

export const getDefaultValues = (
  args: GetDefaultValuesArgs,
): EditJobsiteConfigTabFormData => {
  const {
    jobsite,
    workerCardFormatsOptions,
    cCureControllersOptions,
    cCureDoorsOptions,
    cCureInboundAreasOptions,
    cCureOutboundAreasOptions,
  } = args;

  const cCure = getCCureSectionDefaultValues(
    jobsite,
    workerCardFormatsOptions,
    cCureControllersOptions,
    cCureDoorsOptions,
    cCureInboundAreasOptions,
    cCureOutboundAreasOptions,
  );

  return { cCure };
};

export const getJobsiteUpdateInput = async (
  jobsite: Jobsite,
  data: EditJobsiteConfigTabFormData,
  dirtyFields: DeepMap<EditJobsiteConfigTabFormData, true>,
): Promise<JobsiteUpdateInput> => {
  return ensureNonUndefinedFields<JobsiteUpdateInput>({
    jobsiteId: jobsite.jobsiteId,
    ...getCCureSectionUpdateInput(data.cCure, dirtyFields.cCure),
  });
};
